import React, { useEffect, useState } from "react";
import { getJob, listItems } from "../../../api/migration";
import type {
  MigrationItem,
  MigrationItemResult,
  MigrationJobDetail,
} from "../../../type";
import { Backdrop } from "../../../components/ui/Modal";
import { extractApiError, formatDateTime } from "../helpers";
import JobStatusBadge from "./JobStatusBadge";

interface Props {
  jobId: number | null;
  onClose: () => void;
}

type Tab = "inserted" | "skipped" | "failed";

const RESULT_STYLES: Record<MigrationItemResult, string> = {
  inserted: "text-green-700",
  skipped: "text-gray-600",
  failed: "text-red-700",
};

const MigrationJobDetailModal: React.FC<Props> = ({ jobId, onClose }) => {
  const [detail, setDetail] = useState<MigrationJobDetail | null>(null);
  const [items, setItems] = useState<MigrationItem[]>([]);
  const [itemsTab, setItemsTab] = useState<Tab>("failed");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the detail when the modal opens and whenever jobId changes.
  useEffect(() => {
    if (jobId == null) return;
    setDetail(null);
    setItems([]);
    setError(null);
    setIsLoading(true);
    (async () => {
      try {
        const data = await getJob(jobId);
        setDetail(data);
      } catch (err) {
        setError(extractApiError(err, "Failed to load job."));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [jobId]);

  // Load items for the currently-selected tab.
  useEffect(() => {
    if (jobId == null) return;
    (async () => {
      try {
        const data = await listItems(jobId, { result: itemsTab });
        setItems(data.results);
      } catch (err) {
        setError(extractApiError(err, "Failed to load items."));
      }
    })();
  }, [jobId, itemsTab]);

  if (jobId == null) return null;

  return (
    <Backdrop onClose={onClose} size="2xl">
      <div className="flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Job #{jobId} detail
            </h3>
            {detail && (
              <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                <JobStatusBadge status={detail.status} />
                <span>
                  by {detail.created_by_email ?? "(deleted)"} ·{" "}
                  {formatDateTime(detail.created_at)}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Close
          </button>
        </div>

        <div className="flex-1 p-5 overflow-y-auto space-y-5">
          {isLoading && <p className="text-sm text-gray-500">Loading...</p>}
          {error && (
            <div
              className="p-3 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
              role="alert"
            >
              {error}
            </div>
          )}
          {detail && (
            <>
              <div className="grid gap-3 text-sm md:grid-cols-4">
                <Stat label="Matched" value={detail.total_count} />
                <Stat label="Inserted" value={detail.inserted_count} />
                <Stat label="Skipped" value={detail.skipped_count} />
                <Stat label="Failed" value={detail.failed_count} tone="red" />
                <Stat
                  label="Cascade Metadata"
                  value={detail.cascading_metadata_inserted}
                />
                <Stat
                  label="Scheduled"
                  value={
                    detail.scheduled_at
                      ? formatDateTime(detail.scheduled_at)
                      : "—"
                  }
                  isDate
                />
                <Stat
                  label="Started"
                  value={
                    detail.started_at ? formatDateTime(detail.started_at) : "—"
                  }
                  isDate
                />
                <Stat
                  label="Finished"
                  value={
                    detail.finished_at ? formatDateTime(detail.finished_at) : "—"
                  }
                  isDate
                />
              </div>

              {detail.error_message && (
                <div className="p-3 text-xs font-mono text-red-800 border border-red-200 rounded-lg bg-red-50">
                  {detail.error_message}
                </div>
              )}

              <div>
                <h4 className="mb-2 text-sm font-semibold text-gray-900">
                  Selection
                </h4>
                <pre className="p-3 overflow-x-auto text-xs bg-gray-50 border border-gray-200 rounded-lg">
                  {JSON.stringify(detail.selection, null, 2)}
                </pre>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-900">Items</h4>
                  <div className="flex gap-1">
                    {(["failed", "inserted", "skipped"] as Tab[]).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setItemsTab(t)}
                        className={`px-2 py-1 text-xs rounded border ${
                          itemsTab === t
                            ? "bg-blue-50 border-blue-300 text-blue-800"
                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="text-gray-500 uppercase bg-gray-50">
                      <tr>
                        <th className="px-2 py-1">Row PK</th>
                        <th className="px-2 py-1">Result</th>
                        <th className="px-2 py-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 && (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-2 py-4 text-center text-gray-400"
                          >
                            No items in this bucket.
                          </td>
                        </tr>
                      )}
                      {items.map((item) => (
                        <tr key={item.id} className="border-t border-gray-100">
                          <td className="px-2 py-1 text-gray-900">
                            {item.target_model}/{item.target_pk}
                          </td>
                          <td
                            className={`px-2 py-1 ${RESULT_STYLES[item.result]}`}
                          >
                            {item.result}
                          </td>
                          <td className="px-2 py-1 text-gray-600">
                            {item.reason || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Backdrop>
  );
};

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: "red";
  isDate?: boolean;
}> = ({ label, value, tone, isDate }) => (
  <div>
    <dt className="text-xs text-gray-500 uppercase">{label}</dt>
    <dd
      className={`${isDate ? "text-xs" : "text-sm"} ${
        tone === "red" ? "text-red-700 font-medium" : "text-gray-900"
      }`}
    >
      {value}
    </dd>
  </div>
);

export default MigrationJobDetailModal;
